import React from "react";
import { HoverCard, Pill, DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME, joinClasses } from "./ResultsPrimitives.jsx";
import useAccess from "../hooks/useAccess.jsx";

const chipToneMap = {
  compartment: {
    className: "border-[var(--dispatch-chip-compartment-border)] bg-[image:var(--dispatch-chip-compartment-surface)] text-[#39567e]",
    tone: "soft",
  },
  occupancy: {
    className: "border-[var(--dispatch-chip-occupancy-border)] bg-[image:var(--dispatch-chip-occupancy-surface)] text-[#485d78]",
    tone: "outline-solid",
  },
  dist: {
    className: "border-[var(--dispatch-chip-distance-border)] bg-[image:var(--dispatch-chip-distance-surface)] text-[#31463c]",
    tone: "soft",
  },
  dur: {
    className: "border-[var(--dispatch-chip-duration-border)] bg-[image:var(--dispatch-chip-duration-surface)] text-[#496c9b]",
    tone: "soft",
  },
  grain: {
    className: "text-[#31463c]",
    tone: "info",
  },
  qty: {
    className: "border-[var(--dispatch-chip-occupancy-border)] bg-[image:var(--dispatch-chip-occupancy-surface)] text-[#485d78]",
    tone: "outline-solid",
  },
  svc: {
    className: "border-[var(--dispatch-chip-service-border)] bg-[image:var(--dispatch-chip-service-surface)] text-[#41545a]",
    tone: "neutral",
  },
  meunerie: {
    className: "border-[var(--dispatch-chip-amber-border)] bg-[image:var(--dispatch-chip-amber-surface)] text-[#92400e]",
    tone: "amber",
  },
  aspirator_time: {
    className: "border-[var(--dispatch-chip-orange-border)] bg-[image:var(--dispatch-chip-orange-surface)] text-[#c2410c]",
    tone: "aspiration",
  },
  manual_buffer: {
    className: "border-[var(--dispatch-chip-yellow-border)] bg-[image:var(--dispatch-chip-yellow-surface)] text-[#b45309]",
    tone: "buffer",
  },
  truck_incident: {
    className: "border-red-300 bg-red-50 text-red-700",
    tone: "danger",
  },
};

const compartmentChipPresentationMap = {
  aspiration: {
    accentColor: "rgba(149,119,46,0.78)",
    tone: "aspiration",
    style: {
      backgroundColor: "rgba(214,203,162,0.62)",
      backgroundImage: "linear-gradient(90deg, rgba(162,186,178,0.72) 0%, rgba(147,170,163,0.62) 44%, rgba(226,218,188,0.58) 56%, rgba(214,203,162,0.62) 100%)",
      borderColor: "rgba(126,136,107,0.28)",
      boxShadow: "0 2px 5px rgba(111,121,93,0.08), inset 0 1px 0 rgba(255,251,235,0.52)",
      color: "rgb(66,73,71)",
    },
  },
  delivery: {
    accentColor: "var(--dispatch-segment-delivery-accent, rgba(var(--route-delivery-rgb),0.82))",
    tone: "soft",
    style: {
      backgroundColor: "var(--dispatch-compartment-delivery-fill, rgba(var(--route-delivery-rgb),0.14))",
      borderColor: "var(--dispatch-compartment-delivery-border, rgba(var(--route-delivery-rgb),0.26))",
      color: "var(--dispatch-segment-delivery-ink, var(--route-delivery-strong))",
    },
  },
  loading: {
    accentColor: "var(--dispatch-segment-loading-accent, rgba(var(--route-loading-rgb),0.82))",
    tone: "soft",
    style: {
      backgroundColor: "var(--dispatch-compartment-loading-fill, rgba(var(--route-loading-rgb),0.14))",
      borderColor: "var(--dispatch-compartment-loading-border, rgba(var(--route-loading-rgb),0.28))",
      color: "var(--dispatch-segment-loading-ink, var(--route-loading-strong))",
    },
  },
  mixed: {
    accentColor: "rgba(148,163,184,0.82)",
    tone: "soft",
    style: {
      backgroundImage: "linear-gradient(135deg, var(--dispatch-compartment-loading-fill, rgba(var(--route-loading-rgb),0.16)), var(--dispatch-compartment-delivery-fill, rgba(var(--route-delivery-rgb),0.14)))",
      borderColor: "rgba(100,116,139,0.22)",
      color: "#475569",
    },
  },
};

const chipHoverAccentColorMap = {
  compartment: "rgba(57,86,126,0.82)",
  occupancy: "rgba(72,93,120,0.82)",
  dist: "rgba(49,70,60,0.82)",
  dur: "rgba(73,108,155,0.82)",
  grain: "rgba(49,70,60,0.82)",
  qty: "rgba(72,93,120,0.82)",
  svc: "rgba(65,84,90,0.82)",
  meunerie: "rgba(180,83,9,0.82)",
  aspirator_time: "rgba(234,88,12,0.82)",
  manual_buffer: "rgba(217,119,6,0.82)",
  truck_incident: "rgba(185,28,28,0.82)",
};

const defaultChipColumnClassNameMap = {
  compartment: "lg:col-start-1",
  grain: "lg:col-start-2",
  qty: "lg:col-start-3",
  dist: "lg:col-start-4",
  svc: "lg:col-start-5",
  meunerie: "lg:col-start-6",
  aspirator_time: "lg:col-start-7",
  dur: "lg:col-start-8",
};

const defaultGridClassName = "lg:w-[48.4rem] lg:grid lg:grid-cols-[15.9rem_4.75rem_4rem_5.5rem_4.5rem_3rem_4.5rem_5rem] lg:items-center lg:gap-x-1 lg:gap-y-0";

const chipVariantMap = {
  "quantity-loading": {
    style: { borderColor: "#fde68a", backgroundImage: "none", backgroundColor: "#fffbeb", color: "#92400e" },
    hoverAccentColor: "#92400e",
  },
  "quantity-delivery": {
    style: { borderColor: "#bfdbfe", backgroundImage: "none", backgroundColor: "#eff6ff", color: "#1e40af" },
    hoverAccentColor: "#1e40af",
  },
  "distance-estimated": {
    className: "border-[rgba(187,108,52,0.24)] bg-[linear-gradient(180deg,rgba(255,243,228,0.98),rgba(255,232,208,0.98))] text-[#935d22]",
    hoverAccentColor: "rgba(187,108,52,0.84)",
  },
  "distance-measured": {
    className: "border-[rgba(190,138,62,0.24)] bg-[linear-gradient(180deg,rgba(255,247,232,0.98),rgba(255,238,214,0.98))] text-[#8d6728]",
    hoverAccentColor: "rgba(190,138,62,0.84)",
  },
  "distance-unavailable": {
    className: "border-[rgba(185,84,72,0.24)] bg-[linear-gradient(180deg,rgba(255,239,236,0.98),rgba(255,226,221,0.98))] text-[#94473f]",
    hoverAccentColor: "rgba(185,84,72,0.84)",
  },
};

function sortRouteCompartmentTokens(tokens = []) {
  return [...tokens].sort((leftToken, rightToken) => leftToken.localeCompare(rightToken, "fr", { numeric: true, sensitivity: "base" }));
}

function formatRouteCompartmentDisplayLabel(label = "") {
  if (typeof label !== "string") {
    return "";
  }

  const tokens = Array.from(new Set(
    label
      .split(",")
      .map((token) => token.trim())
      .filter(Boolean),
  ));

  return sortRouteCompartmentTokens(tokens).join(",");
}

export function getRouteMetaChipDisplayLabel(chip) {
  if (typeof chip?.displayLabel === "string" && chip.displayLabel.trim()) {
    return chip.displayLabel.trim();
  }

  if (typeof chip?.label !== "string") {
    return "";
  }

  if (chip?.kind === "compartment" || chip?.key === "compartment") {
    return formatRouteCompartmentDisplayLabel(chip.label);
  }

  return chip.label;
}

export function resolveRouteMetaChipPresentation(chip) {
  if (chip?.kind === "compartment") {
    const stopType = typeof chip?.stopType === "string" ? chip.stopType.trim().toLowerCase() : "";
    return compartmentChipPresentationMap[stopType] || {
      accentColor: chipHoverAccentColorMap.compartment,
      ...chipToneMap.compartment,
    };
  }

  return {
    accentColor: chipHoverAccentColorMap[chip?.kind] || chipHoverAccentColorMap.grain,
    ...(chipToneMap[chip?.kind] || chipToneMap.grain),
  };
}

function RouteChipNoteContent({ note = "" }) {
  return <p className="m-0 max-w-[24rem] text-[11px] leading-[1.55] text-content-muted">{note}</p>;
}

function RouteChipQuantityBreakdownLines({ lines = [] }) {
  const quantityLines = lines.filter((line) => typeof line?.quantity === "string" && line.quantity.trim());

  if (!quantityLines.length) {
    return null;
  }

  const hasOrderColumn = quantityLines.some((line) => typeof line?.order === "string" && line.order.trim());

  return (
    <div
      className="grid min-w-0 gap-x-2 gap-y-1 text-[12px]"
      style={{
        gridTemplateColumns: hasOrderColumn
          ? "minmax(0, 1fr) minmax(0, max-content) 4.5rem auto"
          : "minmax(0, 1fr) 4.5rem auto",
      }}
    >
      {quantityLines.map((line, index) => {
        const compartments = typeof line.compartments === "string" ? line.compartments.trim() : "";
        const order = typeof line.order === "string" ? line.order.trim() : "";

        return (
          <React.Fragment key={index}>
            <span
              className="min-w-0 truncate font-semibold text-content-ink"
              title={typeof line.label === "string" ? line.label : undefined}
            >
              {line.label}
            </span>
            {hasOrderColumn ? (
              <span
                className="min-w-0 truncate font-semibold tabular-nums text-content-copy"
                title={order || undefined}
              >
                {order}
              </span>
            ) : null}
            <span className="whitespace-nowrap text-right font-medium tabular-nums text-content-ink">
              {line.quantity}
            </span>
            <span className="whitespace-nowrap text-content-muted">
              {compartments ? `· ${compartments}` : ""}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function RouteChipFormulaContent({ details: fullDetails = {} }) {
  const { hasPermission } = useAccess();
  // Variante sans les valeurs dont le droit de lecture manque (ex. débit camion).
  const details = fullDetails?.restricted && !hasPermission(fullDetails.restricted.permission)
    ? { ...fullDetails, ...fullDetails.restricted }
    : fullDetails;
  const lines = Array.isArray(details?.lines) ? details.lines : [];
  const note = typeof details?.note === "string" && details.note.trim() ? details.note.trim() : "";
  const total = typeof details?.total === "string" && details.total.trim() ? details.total.trim() : "";
  const totalLabel = typeof details?.totalLabel === "string" && details.totalLabel.trim() ? details.totalLabel.trim() : "Total";
  const useQuantityBreakdownLayout = details?.layout === "quantity-breakdown"
    || lines.some((line) => typeof line?.quantity === "string" && line.quantity.trim());

  return (
    <div className="flex min-w-0 max-w-full flex-col gap-1.5">
      {useQuantityBreakdownLayout ? (
        <RouteChipQuantityBreakdownLines lines={lines} />
      ) : lines.map((line, index) => {
        if (!line?.label && !line?.secondary && !line?.value) {
          return <div key={index} className="h-1" />;
        }

        const hasSecondary = typeof line?.secondary === "string" && line.secondary.trim();

        return (
          <div
            key={index}
            className="grid min-w-0 items-center gap-x-2 text-[12px]"
            style={{
              gridTemplateColumns: hasSecondary
                ? "minmax(0, max-content) minmax(0, max-content) minmax(0, 1fr)"
                : "minmax(0, 1fr) auto",
            }}
          >
            <span
              className="min-w-0 truncate font-semibold text-content-ink"
              title={typeof line.label === "string" ? line.label : undefined}
            >
              {line.label}
            </span>
            {hasSecondary ? (
              <span
                className="min-w-0 truncate font-semibold text-content-copy"
                title={line.secondary}
              >
                {line.secondary}
              </span>
            ) : null}
            <span className={joinClasses([
              "min-w-0 truncate text-content-muted",
              hasSecondary ? "text-right" : "shrink-0 whitespace-nowrap text-right",
            ])}>
              {line.operator ? <span className="mr-1 text-content-faint">{line.operator}</span> : null}
              {line.value}
            </span>
          </div>
        );
      })}

      {total ? (
        <div className="mt-0.5 flex items-center justify-between border-t-[0.5px] border-[rgba(0,0,0,0.08)] pt-2">
          <span className="text-[11px] text-content-muted">{totalLabel}</span>
          <span className="text-[14px] font-bold text-content-ink">= {total}</span>
        </div>
      ) : null}

      {note ? (
        <div className="border-t-[0.5px] border-[rgba(0,0,0,0.08)] pt-2">
          <RouteChipNoteContent note={note} />
        </div>
      ) : null}
    </div>
  );
}

export function resolveRouteChipHoverCardTitle(chip) {
  if (typeof chip?.hoverCardTitle === "string" && chip.hoverCardTitle.trim()) {
    return chip.hoverCardTitle.trim();
  }

  if (typeof chip?.title === "string" && chip.title.trim()) {
    return chip.title.trim();
  }

  return chip?.label || "Badge";
}

export function resolveRouteChipHoverCardContent(chip) {
  if (chip?.hoverCardDetails?.type === "formula") {
    return <RouteChipFormulaContent details={chip.hoverCardDetails} />;
  }

  if (chip?.hoverCardDetails?.type === "note" && typeof chip.hoverCardDetails.note === "string" && chip.hoverCardDetails.note.trim()) {
    return <RouteChipNoteContent note={chip.hoverCardDetails.note.trim()} />;
  }

  if (typeof chip?.title === "string" && chip.title.trim()) {
    return <RouteChipNoteContent note={chip.title.trim()} />;
  }

  if (typeof chip?.label === "string" && chip.label.trim()) {
    return <RouteChipNoteContent note={chip.label.trim()} />;
  }

  return null;
}

export default function RouteMetaChipRow({
  chips = [],
  chipClassName = "",
  chipHoverCardMaxWidth = 420,
  chipLabelClassName = "truncate",
  chipColumnClassNameMap = defaultChipColumnClassNameMap,
  className = "",
  compactLayout = false,
  disableHoverCards = false,
  gridClassName = defaultGridClassName,
  // Rendu propre à une pastille (ex. compartiments en carrés) ; null garde la pastille standard.
  renderChipContent = null,
  useTightHoverCard = false,
}) {
  const items = Array.isArray(chips) ? chips.filter(Boolean) : [];
  if (!items.length) {
    return null;
  }

  return (
    <div className={joinClasses([
      "flex flex-wrap items-center gap-x-0.5 gap-y-0.5",
      gridClassName,
      className,
    ])}>
      {items.map((chip) => {
        const chipPresentation = resolveRouteMetaChipPresentation(chip);
        const chipVariant = chipVariantMap[chip.variant] || null;
        const hoverCardContent = resolveRouteChipHoverCardContent(chip);
        const chipColumnClassName = chipColumnClassNameMap[chip.kind] || "";
        const displayLabel = getRouteMetaChipDisplayLabel(chip);

        const chipWrapperClassName = joinClasses([
          compactLayout ? "w-fit min-w-[5.5rem] lg:w-fit lg:justify-self-start" : "min-w-0 lg:w-full lg:justify-self-stretch",
          chipColumnClassName,
        ]);

        const pill = renderChipContent?.(chip) || (
          <Pill
            tone={chipPresentation.tone}
            className={joinClasses([
              compactLayout
                ? "min-h-5 w-auto min-w-[5.5rem] max-w-full justify-center gap-0.75 px-2.5 py-px text-[0.58rem] leading-none font-semibold shadow-none"
                : "min-h-5 w-full max-w-full min-w-0 gap-0.75 px-1.25 py-px text-[0.58rem] leading-none font-semibold shadow-none",
              DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
              chipLabelClassName === "truncate" ? "overflow-hidden" : "overflow-visible",
              chipClassName,
              chipPresentation.className || "",
              chipVariant?.className || "",
            ])}
            data-route-pill="true"
            style={{ ...chipPresentation.style, ...chipVariant?.style }}
            title={hoverCardContent == null ? chip.title || undefined : undefined}
          >
            <span className={chipLabelClassName}>{displayLabel}</span>
          </Pill>
        );

        if (disableHoverCards || hoverCardContent == null) {
          return (
            <span key={chip.key} className={chipWrapperClassName}>
              {pill}
            </span>
          );
        }

        return (
          <span key={chip.key} className={chipWrapperClassName}>
            <HoverCard
              title={resolveRouteChipHoverCardTitle(chip)}
              content={hoverCardContent}
              placement="top"
              delay={120}
              maxWidth={chipHoverCardMaxWidth}
              width={chipHoverCardMaxWidth}
              compactTitle={useTightHoverCard}
              tight={useTightHoverCard}
              accentColor={chipVariant?.hoverAccentColor || chip.hoverAccentColor || chipPresentation.accentColor}
            >
              {pill}
            </HoverCard>
          </span>
        );
      })}
    </div>
  );
}
